import { http } from '@/api/http';
import { unwrap, type SuccessResponseDTO } from '@/api/envelope';
import type { StickerFormat, StickerReleaseManifest, StickerReleaseMode } from './stickerImport';

const BASE = '/admin/api/media/stickers/releases';
export type StickerReleaseStatusName =
  'STAGING' | 'VALIDATING' | 'READY' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'RETIRED';
export interface StickerReleaseStatus {
  releaseId: string;
  status: StickerReleaseStatusName;
  expectedCount: number;
  sourceUploadedCount: number;
  thumbnailUploadedCount: number;
  verifiedAssetCount?: number;
  expectedAssetCount?: number;
  missingAssets: { stickerId: string; sourceUploaded: boolean; thumbnailUploaded: boolean }[];
  error?: string | null;
  publicationId?: string | null;
  version: string;
  /** Echoed by servers that support APPEND; a status without it is a REPLACE release. */
  mode?: StickerReleaseMode | null;
}

export interface StickerPublication {
  publicationId: string;
  releaseId?: string | null;
  operation: string;
  status: string;
  error?: string | null;
  version: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * The request content type per staged object: only an animated source is a WebP. Thumbnails — the 192 px
 * frame-0 poster included — are always PNG, and the server rejects a body whose bytes disagree.
 */
export function uploadContentType(
  kind: 'source' | 'thumbnail',
  format: StickerFormat = 'png',
): 'image/png' | 'image/webp' {
  return kind === 'source' && format === 'webp-anim' ? 'image/webp' : 'image/png';
}

export const stickerReleases = {
  async list(): Promise<StickerReleaseStatus[]> {
    return unwrap((await http.get<SuccessResponseDTO<StickerReleaseStatus[]>>(BASE)).data);
  },
  async create(manifest: StickerReleaseManifest): Promise<StickerReleaseStatus> {
    return unwrap((await http.post<SuccessResponseDTO<StickerReleaseStatus>>(BASE, manifest)).data);
  },
  async status(releaseId: string): Promise<StickerReleaseStatus> {
    return unwrap(
      (
        await http.get<SuccessResponseDTO<StickerReleaseStatus>>(
          `${BASE}/${encodeURIComponent(releaseId)}`,
        )
      ).data,
    );
  },
  async upload(
    releaseId: string,
    stickerId: string,
    kind: 'source' | 'thumbnail',
    file: File,
    format: StickerFormat = 'png',
  ): Promise<void> {
    await http.put(
      `${BASE}/${encodeURIComponent(releaseId)}/assets/${encodeURIComponent(stickerId)}/${kind}`,
      file,
      { headers: { 'Content-Type': uploadContentType(kind, format) } },
    );
  },
  async action(
    releaseId: string,
    action: 'validate' | 'activate' | 'retry' | 'rollback',
  ): Promise<StickerReleaseStatus> {
    return unwrap(
      (
        await http.post<SuccessResponseDTO<StickerReleaseStatus>>(
          `${BASE}/${encodeURIComponent(releaseId)}/${action}`,
        )
      ).data,
    );
  },
  async publications(): Promise<StickerPublication[]> {
    return unwrap(
      (
        await http.get<SuccessResponseDTO<StickerPublication[]>>(
          '/admin/api/media/stickers/publications',
        )
      ).data,
    );
  },
  async retryPublication(publicationId: string): Promise<StickerPublication[]> {
    return unwrap(
      (
        await http.post<SuccessResponseDTO<StickerPublication[]>>(
          `/admin/api/media/stickers/publications/${encodeURIComponent(publicationId)}/retry`,
        )
      ).data,
    );
  },
};
